'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import dynamic from 'next/dynamic'

import type { PresentationCaptureState } from '@/components/common/PdfViewerV2/FullScreenViewer'
import { FullContainerSpinner } from '@/components/common/Spinner'
import { EMPTY_PRESENTATION_SELECTION } from '@/components/office/PresentationViewer/presentationSelection'
import type { RemotePresentationViewerProps } from '@/components/office/PresentationViewer/RemotePresentationViewer'
import type {
  PresentationSelectionState,
  PresentationSlideContextHandle,
} from '@/components/office/PresentationViewer/viewerTypes'

import type { File } from '@/types/File'

const RemotePresentationViewer = dynamic(
  () =>
    import('@/components/office/PresentationViewer/RemotePresentationViewer').then(
      (module) => module.RemotePresentationViewer,
    ),
  { ssr: false, loading: () => <FullContainerSpinner /> },
)

interface PresentationPreviewHostProps {
  file: File
  pageScale: number
  pageScaleMode: RemotePresentationViewerProps['pageScaleMode']
  /** Zero-based slide to reveal when the viewer opens. */
  initialSlideIndex?: number
  /** Changes when the caller explicitly requests the same slide again. */
  initialSlideNavigationKey?: number
  /** Bumped when the surrounding chat consumes the viewer selection
   * (add-to-chat): clears the selected-object overlays. */
  selectionResetNonce?: number
  onPageScaleStateChange: RemotePresentationViewerProps['onPageScaleStateChange']
  /** The shared native-preview load path; receives the slide count. */
  onLoadSuccess: (slideCount: number) => void
  onLoadError: RemotePresentationViewerProps['onLoadError']
  onRenderStart: () => void
  /** Merged capture snapshot for chat viewport context. */
  onStateChange?: (state: PresentationCaptureState) => void
}

/**
 * Presentation branch of the full-screen viewer. Owns every
 * presentation-only concern — the merged capture snapshot, the slide-context
 * handle and the controlled selection — so none of it exists (or re-renders
 * the shared viewer chrome) unless a presentation is actually open.
 */
export const PresentationPreviewHost: React.FC<
  PresentationPreviewHostProps
> = ({
  file,
  pageScale,
  pageScaleMode,
  initialSlideIndex,
  initialSlideNavigationKey,
  selectionResetNonce,
  onPageScaleStateChange,
  onLoadSuccess,
  onLoadError,
  onRenderStart,
  onStateChange,
}) => {
  /** Everything captured so far; each emit sends the merged union. */
  const captureStateRef = useRef<PresentationCaptureState>({})
  const slideContextRef = useRef<PresentationSlideContextHandle | null>(null)
  // The viewer owns its in-viewer selection overlays; hold that selection
  // controlled-after-first-change so a selectionResetNonce bump (add-to-chat
  // consumed it) can clear the overlays. `undefined` means never selected:
  // the viewer stays uncontrolled.
  const [selection, setSelection] = useState<
    PresentationSelectionState | undefined
  >(undefined)
  useEffect(() => {
    captureStateRef.current = {}
    slideContextRef.current = null
    setSelection(undefined)
  }, [file.id])
  const emitState = useCallback(
    (update: Partial<PresentationCaptureState>) => {
      captureStateRef.current = {
        ...captureStateRef.current,
        ...update,
      }
      onStateChange?.(captureStateRef.current)
    },
    [onStateChange],
  )
  const lastSelectionResetNonceRef = useRef(selectionResetNonce)
  useEffect(() => {
    if (
      selectionResetNonce === undefined ||
      selectionResetNonce === lastSelectionResetNonceRef.current
    ) {
      return
    }
    lastSelectionResetNonceRef.current = selectionResetNonce
    // Force-controlled empty even when this instance never observed a
    // selection: a remount resets this state to `undefined` while the
    // committed panel capture still carries the selected objects, so a
    // `previous === undefined` guard would make the clear a permanent no-op.
    // The emit is what actually retracts the committed selection - without
    // it the cleared capture only rides along on the next unrelated emit
    // (a slide change that may never come).
    setSelection(EMPTY_PRESENTATION_SELECTION)
    emitState({ selectedObjectContext: undefined, selection: undefined })
  }, [emitState, selectionResetNonce])
  const handleLoadSuccess = useCallback(
    (slideCount: number, context: PresentationSlideContextHandle) => {
      slideContextRef.current = context
      // The renderer only calls onSlideChange on navigation; seed the
      // resolved initial slide so a deck that is opened (possibly on a
      // citation-selected slide) and never navigated still reports one.
      const fallbackSlideIndex = Math.min(
        initialSlideIndex ?? 0,
        Math.max(slideCount - 1, 0),
      )
      const slideIndex =
        captureStateRef.current.slideIndex ?? fallbackSlideIndex
      emitState({
        aspectRatioLabel: context.aspectRatioLabel,
        outline: context.getSlideOutline(slideIndex),
        slideCount,
        slideIndex,
      })
      onLoadSuccess(slideCount)
    },
    [emitState, onLoadSuccess, initialSlideIndex],
  )
  const handleSlideChange = useCallback(
    (slideIndex: number) => {
      emitState({
        outline: slideContextRef.current?.getSlideOutline(slideIndex),
        slideIndex,
      })
    },
    [emitState],
  )
  const handleSelectionChange = useCallback(
    (nextSelection: PresentationSelectionState) => {
      // Echo into the controlled prop: from here on the viewer renders
      // this selection, so a reset-nonce clear actually unselects.
      setSelection(nextSelection)
      const soleObject =
        nextSelection.objects.length === 1
          ? nextSelection.objects[0]
          : undefined
      emitState({
        selectedObjectContext:
          soleObject !== undefined
            ? slideContextRef.current?.getObjectContext(
                soleObject.slideIndex,
                soleObject.nodeId,
              )
            : undefined,
        selection: nextSelection,
      })
    },
    [emitState],
  )
  return (
    <RemotePresentationViewer
      file={file}
      className="h-full"
      initialSlideIndex={initialSlideIndex}
      initialSlideNavigationKey={initialSlideNavigationKey}
      onPageScaleStateChange={onPageScaleStateChange}
      pageScale={pageScale}
      pageScaleMode={pageScaleMode}
      onLoadSuccess={handleLoadSuccess}
      onLoadError={onLoadError}
      onRenderStart={onRenderStart}
      onSelectionChange={handleSelectionChange}
      onSlideChange={handleSlideChange}
      selection={selection}
    />
  )
}

PresentationPreviewHost.displayName = 'FullScreenViewer.PresentationPreviewHost'
